'use client'

import { useCallback, useEffect, useState } from 'react'
import { createContainer } from 'unstated-next'

import { useFeatureFlag } from './ExperimentationProvider.client'

const usePreviewPanelImpl = () => {
  const [isPanelOpen, setIsPanelOpen] = useState(false)

  const isInternalAdmin =
    useFeatureFlag<'true' | 'false'>({
      key: 'is-internal-admin',
      controlVariant: 'false',
      ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
    }) === 'true'

  const togglePanel = useCallback(() => {
    if (!isInternalAdmin) {
      setIsPanelOpen(false)
    } else {
      setIsPanelOpen(open => !open)
    }
  }, [isInternalAdmin])

  // Hotkey: (Option + Command + /) toggles the preview panel
  useEffect(() => {
    if (!isInternalAdmin) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.metaKey || !e.altKey || e.code !== 'Slash') return

      e.preventDefault()
      togglePanel()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isInternalAdmin, togglePanel])

  return {
    isPanelOpen,
    showPreviewPanel: isInternalAdmin,
    togglePanel,
  }
}

const PreviewPanelContainer = createContainer(usePreviewPanelImpl)

PreviewPanelContainer.Provider.displayName = 'PreviewPanelProviderImpl'

export const PreviewPanelProvider = ({ children }: { children: React.ReactNode }) => {
  return <PreviewPanelContainer.Provider>{children}</PreviewPanelContainer.Provider>
}
export const usePreviewPanel = PreviewPanelContainer.useContainer
